import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CalendarService } from '../services/calendar.service';
import { 
  CalendarData, 
  CalendarEvent, 
  CreateCategoryData, 
  CreateEventData, 
  EventCategory, 
  EventsByDate, 
  FormattedEvent, 
  GroupedEvents, 
  UpdateCategoryData, 
  UpdateEventData 
} from '../types/calendar.types';

export const useCalendar = () => {
  const [calendarData, setCalendarData] = useState<CalendarData>({ events: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await CalendarService.getCalendarData();
      setCalendarData(data);
      setError(null);
    } catch (err) {
      setError('Error fetching calendar data');
      console.error('Error fetching calendar data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create event
  const createEvent = useCallback(async (data: CreateEventData) => {
    try {
      const newEvent = await CalendarService.createEvent(data);
      setCalendarData(prev => ({
        ...prev,
        events: [...prev.events, newEvent]
      }));
      toast.success('Event created successfully');
      return newEvent;
    } catch (err) {
      console.error('Error creating event:', err);
      toast.error('Failed to create event');
      throw err;
    }
  }, []);

  // Update event
  const updateEvent = useCallback(async (eventId: string, data: UpdateEventData) => {
    try {
      const updatedEvent = await CalendarService.updateEvent(eventId, data);
      setCalendarData(prev => ({
        ...prev,
        events: prev.events.map(event => 
          event.id === eventId ? updatedEvent : event
        )
      }));
      toast.success('Event updated successfully');
      return updatedEvent;
    } catch (err) {
      console.error('Error updating event:', err);
      toast.error('Failed to update event');
      throw err;
    }
  }, []);

  // Delete event
  const deleteEvent = useCallback(async (eventId: string) => {
    try {
      await CalendarService.deleteEvent(eventId);
      setCalendarData(prev => ({
        ...prev,
        events: prev.events.filter(event => event.id !== eventId)
      }));
      toast.success('Event deleted successfully');
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
      throw err;
    }
  }, []);

  // Create category
  const createCategory = useCallback(async (data: CreateCategoryData) => {
    try {
      const newCategory = await CalendarService.createCategory(data);
      setCalendarData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory]
      }));
      toast.success('Category created successfully');
      return newCategory;
    } catch (err) {
      console.error('Error creating category:', err);
      toast.error('Failed to create category');
      throw err;
    }
  }, []);

  // Update category
  const updateCategory = useCallback(async (categoryId: string, data: UpdateCategoryData) => {
    try {
      const updatedCategory = await CalendarService.updateCategory(categoryId, data);
      setCalendarData(prev => ({
        ...prev,
        categories: prev.categories.map(category => 
          category.id === categoryId ? updatedCategory : category
        )
      }));
      toast.success('Category updated successfully');
      return updatedCategory;
    } catch (err) {
      console.error('Error updating category:', err);
      toast.error('Failed to update category');
      throw err;
    }
  }, []);

  // Delete category
  const deleteCategory = useCallback(async (categoryId: string) => {
    try {
      await CalendarService.deleteCategory(categoryId);
      setCalendarData(prev => ({
        ...prev,
        categories: prev.categories.filter(category => category.id !== categoryId)
      }));
      toast.success('Category deleted successfully');
    } catch (err) {
      console.error('Error deleting category:', err);
      toast.error('Failed to delete category');
      throw err;
    }
  }, []);

  // Group events by date
  const eventsByDate = useMemo<EventsByDate>(() => {
    const result: EventsByDate = {};
    
    // Filter events by active category if set
    const filteredEvents = activeCategory 
      ? calendarData.events.filter(event => event.categoryId === activeCategory)
      : calendarData.events;
    
    filteredEvents.forEach(event => {
      const dateKey = new Date(event.startDate.toDate()).toDateString();
      if (!result[dateKey]) {
        result[dateKey] = [];
      }
      result[dateKey].push(event);
    });
    
    return result;
  }, [calendarData.events, activeCategory]);

  // Group events by category
  const eventsByCategory = useMemo<GroupedEvents>(() => {
    const result: GroupedEvents = {};
    
    calendarData.events.forEach(event => {
      if (!result[event.categoryId]) {
        result[event.categoryId] = [];
      }
      result[event.categoryId].push(event);
    });
    
    return result;
  }, [calendarData.events]);

  // Format events for the calendar component
  const formattedEvents = useMemo<FormattedEvent[]>(() => {
    return calendarData.events
      .filter(event => !activeCategory || event.categoryId === activeCategory)
      .map(event => {
        const category = calendarData.categories.find(cat => cat.id === event.categoryId);
        return {
          id: event.id,
          eventStart: event.startDate.toDate(),
          eventEnd: event.endDate?.toDate(),
          eventName: event.title,
          eventColor: category?.color || 'gray',
          description: event.description,
          url: event.url,
          categoryId: event.categoryId
        };
      });
  }, [calendarData.events, calendarData.categories, activeCategory]);

  // Get events for a specific date
  const getEventsForDate = useCallback((date: Date) => {
    const dateKey = date.toDateString();
    return eventsByDate[dateKey] || [];
  }, [eventsByDate]);

  // Toggle active category filter
  const toggleCategoryFilter = useCallback((categoryId: string) => {
    setActiveCategory(prev => prev === categoryId ? null : categoryId);
  }, []);

  // Clear active category filter
  const clearCategoryFilter = useCallback(() => {
    setActiveCategory(null);
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  return {
    events: calendarData.events,
    categories: calendarData.categories,
    formattedEvents,
    eventsByDate,
    eventsByCategory,
    activeCategory,
    loading,
    error,
    fetchCalendarData,
    createEvent,
    updateEvent,
    deleteEvent,
    createCategory,
    updateCategory,
    deleteCategory,
    getEventsForDate,
    toggleCategoryFilter,
    clearCategoryFilter
  };
};
